import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PublicProductsAPI, PublicCartAPI, CustomerOrdersAPI, PublicReviewsAPI, getProductImageUrl } from '../../Services/ECommerceAPI';
import { notifyCartUpdated } from '../../Layout/PublicLayout';
import { toast } from 'react-toastify';
import './ProductDetails.css';

const ProductDetails = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [reviewsMeta, setReviewsMeta] = useState({ averageRating: 0, count: 0 });
    const [reviews, setReviews] = useState([]);
    const [reviewForm, setReviewForm] = useState({
        rating: 5,
        title: '',
        comment: '',
        reviewerName: '',
        reviewerEmail: ''
    });
    const [submittingReview, setSubmittingReview] = useState(false);
    const organizationId = 1;

    useEffect(() => {
        checkAuth();
        loadProduct();
    }, [slug]);

    useEffect(() => {
        setSelectedImage(0);
    }, [slug]);

    useEffect(() => {
        const images = product?.images || [];
        if (images.length <= 1) return;
        const handleKey = (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setSelectedImage((i) => (i - 1 + images.length) % images.length);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                setSelectedImage((i) => (i + 1) % images.length);
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [product]);

    const checkAuth = () => {
        const token = sessionStorage.getItem('token');
        setIsAuthenticated(!!token);
    };

    const loadProduct = async () => {
        try {
            setLoading(true);
            let data;
            if (isNaN(slug)) {
                data = await PublicProductsAPI.getProductBySlug(slug, organizationId);
            } else {
                data = await PublicProductsAPI.getProductById(parseInt(slug));
            }
            setProduct(data);
            const pid = data?.productID ?? data?.ProductID;
            if (pid) {
                loadReviews(pid);
            }
        } catch (error) {
            console.error('Error loading product:', error);
            toast.error('Failed to load product');
            navigate('/shop');
        } finally {
            setLoading(false);
        }
    };

    const loadReviews = async (productId) => {
        try {
            setReviewsLoading(true);
            const data = await PublicReviewsAPI.getForProduct(productId);
            setReviewsMeta({
                averageRating: data?.averageRating ?? 0,
                count: data?.count ?? 0
            });
            setReviews(Array.isArray(data?.reviews) ? data.reviews : []);
        } catch (error) {
            console.error('Error loading reviews:', error);
            setReviewsMeta({ averageRating: 0, count: 0 });
            setReviews([]);
        } finally {
            setReviewsLoading(false);
        }
    };

    const submitReview = async (e) => {
        e.preventDefault();
        const productId = product?.productID ?? product?.ProductID;
        if (!productId) return;

        const rating = parseInt(reviewForm.rating) || 0;
        if (rating < 1 || rating > 5) {
            toast.error('Rating must be between 1 and 5');
            return;
        }

        try {
            setSubmittingReview(true);
            await PublicReviewsAPI.createForProduct(productId, {
                rating,
                title: reviewForm.title,
                comment: reviewForm.comment,
                reviewerName: reviewForm.reviewerName,
                reviewerEmail: reviewForm.reviewerEmail
            });
            toast.success('Review submitted (pending approval)');
            setReviewForm((s) => ({ ...s, title: '', comment: '' }));
        } catch (error) {
            console.error('Error submitting review:', error);
            toast.error(error.response?.data?.message || 'Failed to submit review');
        } finally {
            setSubmittingReview(false);
        }
    };

    const renderStars = (value) => {
        const v = Math.round(value || 0);
        return (
            <span className="review-stars" aria-label={`${v} out of 5`}>
                {'★★★★★'.split('').map((_, i) => (
                    <span key={i} className={i < v ? 'star on' : 'star off'}>★</span>
                ))}
            </span>
        );
    };

    /** Returns guest sessionId if guest (so caller can pass to checkout), else null */
    const handleAddToCart = async () => {
        if (!product || product.stockQuantity === 0) {
            toast.error('Product is out of stock');
            return null;
        }

        if (quantity > product.stockQuantity) {
            toast.error(`Only ${product.stockQuantity} items available`);
            return null;
        }

        try {
            if (isAuthenticated) {
                await CustomerOrdersAPI.addCartItem(product.productID, quantity);
                toast.success('Product added to cart!');
                notifyCartUpdated();
                return null;
            }
            // Guest cart
            let sessionId = sessionStorage.getItem('cartSessionId');
            if (!sessionId) {
                const cart = await PublicCartAPI.createCart();
                sessionId = (cart && (cart.sessionID ?? cart.SessionID)) || null;
                if (!sessionId) {
                    toast.error('Could not create cart');
                    return null;
                }
                sessionStorage.setItem('cartSessionId', sessionId);
            }
            await PublicCartAPI.addItem(sessionId, product.productID, quantity);
            toast.success('Product added to cart!');
            notifyCartUpdated();
            return sessionId;
        } catch (error) {
            console.error('Error adding to cart:', error);
            toast.error(error.response?.data?.message || 'Failed to add product to cart');
            return null;
        }
    };

    if (loading) {
        return <div className="loading-container">Loading product...</div>;
    }

    if (!product) {
        return <div className="error-container">Product not found</div>;
    }

    const images = product.images || [];
    const displayImages = images.length ? images : [];
    const currentImage = displayImages[selectedImage];
    const hasMultiple = displayImages.length > 1;

    const goPrev = () => {
        setSelectedImage((i) => (i - 1 + displayImages.length) % displayImages.length);
    };
    const goNext = () => {
        setSelectedImage((i) => (i + 1) % displayImages.length);
    };

    return (
        <div className="product-details-container">
            <div className="product-details-content">
                {/* Images Section - Carousel with left/right slides */}
                <div className="product-images">
                    <div className="main-image-carousel">
                        <div className="main-image">
                            {currentImage ? (
                                <img
                                    src={getProductImageUrl(currentImage.imageUrl)}
                                    alt={`${product.name} - ${selectedImage + 1} of ${displayImages.length}`}
                                    onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/600x600?text=No+Image';
                                    }}
                                />
                            ) : (
                                <div className="no-image-large">No Image Available</div>
                            )}
                        </div>
                        {hasMultiple && (
                            <>
                                <button
                                    type="button"
                                    className="carousel-btn carousel-prev"
                                    onClick={goPrev}
                                    aria-label="Previous image"
                                >
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6"/>
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    className="carousel-btn carousel-next"
                                    onClick={goNext}
                                    aria-label="Next image"
                                >
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6"/>
                                    </svg>
                                </button>
                                <span className="carousel-counter">{selectedImage + 1} / {displayImages.length}</span>
                            </>
                        )}
                    </div>
                    {displayImages.length > 1 && (
                        <div className="thumbnail-images">
                            {displayImages.map((img, index) => (
                                <button
                                    key={img.imageID}
                                    type="button"
                                    className={`thumbnail-btn ${selectedImage === index ? 'active' : ''}`}
                                    onClick={() => setSelectedImage(index)}
                                    aria-label={`View image ${index + 1}`}
                                >
                                    <img
                                        src={getProductImageUrl(img.imageUrl)}
                                        alt={`${product.name} ${index + 1}`}
                                        onError={(e) => {
                                            e.target.src = 'https://via.placeholder.com/100x100?text=No+Image';
                                        }}
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Product Info Section */}
                <div className="product-info-section">
                    <h1 className="product-title">{product.name}</h1>
                    <p className="product-category">{product.category?.name || 'Uncategorized'}</p>

                    <div className="product-price-section">
                        {product.compareAtPrice && product.compareAtPrice > product.price ? (
                            <>
                                <span className="old-price">${product.compareAtPrice.toFixed(2)}</span>
                                <span className="current-price">${product.price.toFixed(2)}</span>
                                <span className="discount-badge-detail">{Math.round((1 - product.price / product.compareAtPrice) * 100)}% off</span>
                            </>
                        ) : (
                            <span className="current-price">${product.price.toFixed(2)}</span>
                        )}
                    </div>

                    <div className="stock-info">
                        {product.stockQuantity > 0 ? (
                            <span className="in-stock">In Stock ({product.stockQuantity} available)</span>
                        ) : (
                            <span className="out-of-stock">Out of Stock</span>
                        )}
                    </div>

                    {product.description && (
                        <div className="product-description">
                            <h3>Description</h3>
                            <p>{product.description}</p>
                        </div>
                    )}

                    {product.shortDescription && (
                        <div className="product-short-description">
                            <p>{product.shortDescription}</p>
                        </div>
                    )}

                    {/* Add to Cart Section */}
                    {product.stockQuantity > 0 && (
                        <div className="add-to-cart-section">
                            <div className="quantity-selector">
                                <label>Quantity:</label>
                                <div className="quantity-controls">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        disabled={quantity <= 1}
                                    >
                                        -
                                    </button>
                                    <input
                                        type="number"
                                        value={quantity}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value) || 1;
                                            setQuantity(Math.max(1, Math.min(val, product.stockQuantity)));
                                        }}
                                        min="1"
                                        max={product.stockQuantity}
                                    />
                                    <button
                                        onClick={() => setQuantity(Math.min(product.stockQuantity, quantity + 1))}
                                        disabled={quantity >= product.stockQuantity}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            <button
                                className="add-to-cart-button"
                                onClick={handleAddToCart}
                            >
                                Add to Cart
                            </button>

                            <button
                                className="buy-now-button"
                                onClick={async () => {
                                    try {
                                        const guestSessionId = await handleAddToCart();
                                        if (guestSessionId) {
                                            sessionStorage.setItem('cartSessionId', guestSessionId);
                                            navigate(`/checkout?sid=${encodeURIComponent(guestSessionId)}`, { state: { guestSessionId } });
                                        } else if (sessionStorage.getItem('token') || sessionStorage.getItem('cartSessionId')) {
                                            navigate('/checkout');
                                        }
                                    } catch (_) {
                                        // handleAddToCart already shows toast on error
                                    }
                                }}
                            >
                                Buy Now
                            </button>
                        </div>
                    )}

                    {/* Product Details */}
                    <div className="product-details-info">
                        <h3>Product Details</h3>
                        <table>
                            {product.sku && (
                                <tr>
                                    <td>SKU:</td>
                                    <td>{product.sku}</td>
                                </tr>
                            )}
                            {product.brand && (
                                <tr>
                                    <td>Brand:</td>
                                    <td>{product.brand}</td>
                                </tr>
                            )}
                            {product.weight && (
                                <tr>
                                    <td>Weight:</td>
                                    <td>{product.weight}</td>
                                </tr>
                            )}
                            {product.dimensions && (
                                <tr>
                                    <td>Dimensions:</td>
                                    <td>{product.dimensions}</td>
                                </tr>
                            )}
                        </table>
                    </div>

                    {/* Reviews */}
                    <div className="product-reviews">
                        <div className="reviews-head">
                            <h3>Customer Reviews</h3>
                            <div className="reviews-meta">
                                {renderStars(reviewsMeta.averageRating)}
                                <span className="reviews-avg">{(reviewsMeta.averageRating || 0).toFixed(1)}</span>
                                <span className="reviews-count">({reviewsMeta.count || 0})</span>
                            </div>
                        </div>

                        <div className="reviews-list">
                            {reviewsLoading ? (
                                <div className="reviews-loading">Loading reviews...</div>
                            ) : reviews.length === 0 ? (
                                <div className="reviews-empty">No reviews yet. Be the first to review this product.</div>
                            ) : (
                                reviews.map((r) => (
                                    <div key={r.reviewID ?? r.ReviewID} className="review-card">
                                        <div className="review-top">
                                            <div className="review-who">
                                                <div className="review-name">{r.reviewerName || r.ReviewerName || 'Anonymous'}</div>
                                                <div className="review-date">{new Date(r.createdDate || r.CreatedDate).toLocaleDateString()}</div>
                                            </div>
                                            <div className="review-rating">{renderStars(r.rating || r.Rating)}</div>
                                        </div>
                                        {(r.title || r.Title) && <div className="review-title">{r.title || r.Title}</div>}
                                        {(r.comment || r.Comment) && <div className="review-comment">{r.comment || r.Comment}</div>}
                                    </div>
                                ))
                            )}
                        </div>

                        <form className="review-form" onSubmit={submitReview}>
                            <div className="review-form-row">
                                <label>Rating</label>
                                <select
                                    value={reviewForm.rating}
                                    onChange={(e) => setReviewForm((s) => ({ ...s, rating: e.target.value }))}
                                >
                                    <option value={5}>5 - Excellent</option>
                                    <option value={4}>4 - Good</option>
                                    <option value={3}>3 - Average</option>
                                    <option value={2}>2 - Poor</option>
                                    <option value={1}>1 - Bad</option>
                                </select>
                            </div>

                            <div className="review-form-grid">
                                <div className="review-form-row">
                                    <label>Your name</label>
                                    <input
                                        type="text"
                                        value={reviewForm.reviewerName}
                                        onChange={(e) => setReviewForm((s) => ({ ...s, reviewerName: e.target.value }))}
                                        placeholder="e.g. John"
                                    />
                                </div>
                                <div className="review-form-row">
                                    <label>Email (optional)</label>
                                    <input
                                        type="email"
                                        value={reviewForm.reviewerEmail}
                                        onChange={(e) => setReviewForm((s) => ({ ...s, reviewerEmail: e.target.value }))}
                                        placeholder="e.g. john@email.com"
                                    />
                                </div>
                            </div>

                            <div className="review-form-row">
                                <label>Title (optional)</label>
                                <input
                                    type="text"
                                    value={reviewForm.title}
                                    onChange={(e) => setReviewForm((s) => ({ ...s, title: e.target.value }))}
                                    placeholder="Short summary"
                                />
                            </div>

                            <div className="review-form-row">
                                <label>Comment</label>
                                <textarea
                                    value={reviewForm.comment}
                                    onChange={(e) => setReviewForm((s) => ({ ...s, comment: e.target.value }))}
                                    placeholder="Write your review..."
                                    rows={4}
                                    required
                                />
                            </div>

                            <button className="common-button" type="submit" disabled={submittingReview}>
                                {submittingReview ? 'Submitting…' : 'Submit Review'}
                            </button>
                            <div className="review-note">Note: reviews appear after admin approval.</div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetails;
